/** @format */

import {Box, Grid, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import withStyles from '@mui/styles/withStyles';

import BoxPlate from 'components/plates/BoxPlate';
import {
  ADS_SECTION,
  AD_GROUPS_SECTION,
  CAMPAIGNS_SECTION,
  KEYWORDS_SECTION,
  SITELINKS_SECTION,
  locationSections,
} from 'helpers/reportHelpers';

import Account from './Account';
import Ad from './Ad';
import AdGroup from './AdGroup';
import Campaign from './Campaign';
import Keyword from './Keyword';
import ObjectLink from './ObjectLink';
import ObjectHost from './ObjectHost';
import ShowMore from './ShowMore';
import Sitelink from './Sitelink';
import styles from './styles';
import {CAMPAIGN_FIELD, OBJECT_HOST_FIELD, OBJECT_LINK_FIELD} from '../constants';
import {GroupedReport, ReportItemsType} from '../types';
import {groupField, reportItemGroups} from '../helpers';

interface Props {
  sensorName: string;
  reportItems?: ReportItemsType;
  isError: boolean;
}

const Card = ({sensorName, isError, reportItems}: Props) => {
  const {t} = useTranslation();

  if (!reportItems) {
    return null;
  }

  const renderTitle = (title: string) => <Typography>{title}</Typography>;

  const renderGroupField = () => {
    const grouped = groupField(sensorName);

    return (
      <>
        {grouped === CAMPAIGN_FIELD && (
          <Box mt={4}>
            {renderTitle(t('Кампания', 'Кампания'))}
            <Box mt={2}>
              <Campaign reportItemGroup={reportItems} sensorName={sensorName} />
            </Box>
          </Box>
        )}

        {grouped === OBJECT_LINK_FIELD && (
          <Box mt={4}>
            <ObjectLink reportItem={reportItems[0]} sensorName={sensorName} />
          </Box>
        )}
        {grouped === OBJECT_HOST_FIELD && (
          <Box mt={4}>
            <ObjectHost reportItem={reportItems[0]} sensorName={sensorName} />
          </Box>
        )}
      </>
    );
  };

  const renderLocationSections = () => {
    const locationSectionsResult = locationSections(sensorName);

    return (
      <>
        {locationSectionsResult.includes(CAMPAIGNS_SECTION) && (
          <ShowMore
            isError={isError}
            render={(vs: GroupedReport, comment: string) => (
              <Campaign comment={comment} reportItemGroup={vs} sensorName={sensorName} />
            )}
            reportItemGroups={reportItemGroups('campaignId', reportItems)}
            sensorName={sensorName}
            title={t('Кампании', 'Кампании')}
          />
        )}

        {locationSectionsResult.includes(AD_GROUPS_SECTION) && (
          <ShowMore
            isError={isError}
            render={(vs: GroupedReport) => <AdGroup reportItemGroup={vs} sensorName={sensorName} />}
            reportItemGroups={reportItemGroups('adGroupId', reportItems)}
            title={t('Группы объявлений', 'Группы объявлений')}
          />
        )}

        {locationSectionsResult.includes(ADS_SECTION) && (
          <ShowMore
            isError={isError}
            render={(vs: GroupedReport) => <Ad reportItemGroup={vs} sensorName={sensorName} />}
            reportItemGroups={reportItemGroups('adId', reportItems)}
            sensorName={sensorName}
            title={t('Объявления', 'Объявления')}
          />
        )}

        {locationSectionsResult.includes(SITELINKS_SECTION) && (
          <ShowMore
            isError={isError}
            render={(vs: GroupedReport) => (
              <Sitelink reportItemGroup={vs} sensorName={sensorName} />
            )}
            reportItemGroups={reportItemGroups('sitelinkId', reportItems)}
            sensorName={sensorName}
            title={t('Быстрые ссылки', 'Быстрые ссылки')}
          />
        )}

        {locationSectionsResult.includes(KEYWORDS_SECTION) && (
          <ShowMore
            isError={isError}
            render={(vs: GroupedReport) => <Keyword reportItemGroup={vs} sensorName={sensorName} />}
            reportItemGroups={reportItemGroups('keywordId', reportItems)}
            sensorName={sensorName}
            title={t('Ключевые фразы', 'Ключевые фразы')}
          />
        )}
      </>
    );
  };

  return (
    <Grid item xs={12}>
      <BoxPlate>
        <Account reportItemGroup={reportItems} sensorName={sensorName} />
        {renderGroupField()}
        {renderLocationSections()}
      </BoxPlate>
    </Grid>
  );
};

export default withStyles(styles)(Card);
